import React from "react";
import { CheckIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader } from "./ui/card";
import { Badge } from "./ui/badge";
import stripePromise from "../lib/stripe";
import { API_ENDPOINTS } from "../config/api";
import { DatabaseService } from "../services/database";
import { useState, useEffect } from "react";

interface PricingTierProps {
  name: string;
  price: string;
  originalPrice?: string;
  description: string;
  features: Array<{
    text: string;
    included: boolean;
  }>;
  ctaText: string;
  highlighted?: boolean;
  onCtaClick?: () => void;
  disabled?: boolean;
  billingCycle?: string;
  discount?: string;
}

const PricingTier: React.FC<PricingTierProps> = ({
  name,
  price,
  originalPrice,
  description,
  features,
  ctaText,
  highlighted = false,
  onCtaClick,
  disabled = false,
  billingCycle,
  discount,
}) => {
  return (
    <Card
      className={`relative transition-all duration-300 transform hover:scale-[1.02] ${
        highlighted
          ? "bg-black text-white border-2 border-black shadow-xl"
          : "bg-white border-2 border-gray-200 shadow-sm hover:shadow-md hover:border-gray-300"
      }`}
    >
      {highlighted && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
          <Badge className="bg-black text-white border-black px-4 py-1 text-sm font-medium">
            Most Popular
          </Badge>
        </div>
      )}

      <CardHeader>
        <div className="flex items-center gap-3 mb-2">
          <h3 className="text-xl font-semibold leading-tight">{name}</h3>
          {discount && (
            <Badge
              variant="outline"
              className={`text-xs ${
                highlighted
                  ? "border-white text-white"
                  : "border-green-500 text-green-600 bg-green-50"
              }`}
            >
              {discount}
            </Badge>
          )}
        </div>
        <div className="mb-4">
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-bold">{price}</span>
            {originalPrice && (
              <span className="text-lg line-through opacity-50">
                {originalPrice}
              </span>
            )}
            {price !== "Contact Us" && (
              <span className="text-base opacity-70">
                {billingCycle || "/month"}
              </span>
            )}
          </div>
        </div>
        <p
          className={`text-sm ${
            highlighted ? "text-gray-300" : "text-gray-600"
          }`}
        >
          {description}
        </p>
      </CardHeader>

      <CardContent>
        <ul className="mb-8 space-y-4">
          {features.map((feature, index) => (
            <li key={index} className="flex items-start">
              {feature.included ? (
                <CheckIcon
                  className={`h-5 w-5 flex-shrink-0 mt-0.5 ${
                    highlighted ? "text-gray-300" : "text-gray-600"
                  }`}
                />
              ) : (
                <XMarkIcon className="h-5 w-5 flex-shrink-0 mt-0.5 text-gray-400" />
              )}
              <span
                className={`ml-3 ${!feature.included ? "text-gray-400" : ""}`}
              >
                {feature.text}
              </span>
            </li>
          ))}
        </ul>

        <Button
          onClick={disabled ? undefined : onCtaClick}
          className={`w-full transition-colors duration-200 ${
            disabled
              ? highlighted
                ? "bg-gray-100 text-gray-500"
                : "bg-gray-300 text-gray-500"
              : highlighted
              ? "bg-white text-black hover:bg-black hover:text-white hover:ring-2 hover:ring-white"
              : "bg-black text-white hover:bg-white hover:text-black hover:ring-1 hover:ring-black"
          }`}
          disabled={disabled}
          size="lg"
        >
          {ctaText}
        </Button>
      </CardContent>
    </Card>
  );
};

interface PricingSectionProps {
  onFreeSignUp?: () => void;
  onProUpgrade?: () => void;
  onEnterprise?: () => void;
}

const PricingSection: React.FC<PricingSectionProps> = ({
  onFreeSignUp,
  onProUpgrade,
  onEnterprise,
}) => {
  const { user, subscriptionData, subscriptionLoading } = useAuth();
  const [isFullCycle, setIsFullCycle] = useState(false);

  // Check for stored billing cycle preference on mount
  useEffect(() => {
    try {
      const storedFullCycle = localStorage.getItem("upgradeFullCycle");
      if (storedFullCycle === "true") {
        setIsFullCycle(true);
        // Clean up the stored preference
        localStorage.removeItem("upgradeFullCycle");
      }
    } catch (error) {
      // Ignore localStorage errors
    }
  }, []);

  // Remove local subscription state and fetching since we get it from AuthContext
  // const [subscriptionPlan, setSubscriptionPlan] = useState<string>("free");

  // useEffect(() => {
  //   const fetchUserSubscriptionPlan = async () => {
  //     if (user?.id) {
  //       const plan = await DatabaseService.getUserSubscriptionPlan(user.id);
  //       setSubscriptionPlan(plan || "free");
  //     }
  //   };
  //   fetchUserSubscriptionPlan();
  // }, [user?.id]);

  const handleProUpgrade = async () => {
    if (!user) {
      // Store intent to upgrade so we can trigger Stripe after sign-in
      localStorage.setItem("upgradePro", "true");
      // Store billing cycle preference for after sign-in
      localStorage.setItem("upgradeFullCycle", isFullCycle.toString());
      // If user is not logged in, redirect to sign up first
      if (onFreeSignUp) {
        onFreeSignUp();
      }
      return;
    }

    // If user is already pro, don't do anything
    if (subscriptionData?.plan === "pro") {
      return;
    }

    try {
      // Select the appropriate price ID based on billing cycle
      const priceId = isFullCycle
        ? process.env.REACT_APP_STRIPE_PRICE_ID_ANNUALLY // 12 months at $8/month (one-time payment, non-recurring)
        : process.env.REACT_APP_STRIPE_PRICE_ID_MONTHLY; // Monthly at $10/month (recurring subscription)

      // Create Stripe checkout session
      const response = await fetch(API_ENDPOINTS.createCheckoutSession, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userId: user.id,
          mode: "subscription", // Both monthly and annual are recurring subscriptions
          line_items: [
            {
              price: priceId,
              quantity: 1,
            },
          ],
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.detail || "Failed to create checkout session"
        );
      }

      const { sessionId } = await response.json();

      // Redirect to Stripe Checkout
      const stripe = await stripePromise;
      if (stripe) {
        const { error } = await stripe.redirectToCheckout({ sessionId });
        if (error) {
          throw error;
        }
      }
    } catch (error) {
      console.error("Error creating checkout session:", error);
      // Handle error - maybe show a toast notification
    }
  };

  const handleEnterprise = () => {
    // Open contact form or redirect to contact page
    window.open(
      "mailto:team@essaypilot.io?subject=Enterprise Inquiry",
      "_blank"
    );
  };

  const handleFreeClick = async () => {
    if (!user) {
      // Not signed in, show sign up modal
      if (onFreeSignUp) {
        onFreeSignUp();
      }
      return;
    }

    if (subscriptionData?.plan === "pro") {
      // Pro user clicking free tier - redirect to customer portal to downgrade
      try {
        const response = await fetch(API_ENDPOINTS.createPortalSession, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email: user.email }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.detail || "Failed to create portal session"
          );
        }

        const { url } = await response.json();
        if (url) {
          window.location.href = url;
        }
      } catch (error) {
        console.error("Error opening customer portal:", error);
      }
    }
    // If user is free, do nothing (already on free plan)
  };

  // Show loading state while fetching subscription plan
  const isProUser = Boolean(user && subscriptionData?.plan === "pro");
  const isFreeUser = Boolean(user && subscriptionData?.plan === "free");

  return (
    <div className="px-6 pt-24 pb-24 bg-white dotted-background">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-2xl lg:text-3xl font-semibold tracking-tight text-gray-900 mb-4 leading-tight">
            Choose Your Application Journey
          </h2>
          <p className="text-base lg:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed mb-8">
            Start with essay review and scale up to complete college application
            support with your AI copilot
          </p>

          {/* Billing Toggle */}
          <div className="flex items-center justify-center gap-4 mb-8">
            <span
              className={`text-sm font-medium ${
                !isFullCycle ? "text-gray-900" : "text-gray-500"
              }`}
            >
              Monthly
            </span>
            <button
              onClick={() => setIsFullCycle(!isFullCycle)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                isFullCycle ? "bg-gray-900" : "bg-gray-200"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  isFullCycle ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
            <span
              className={`text-sm font-medium ${
                isFullCycle ? "text-gray-900" : "text-gray-500"
              }`}
            >
              Full Cycle
            </span>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
          <PricingTier
            name="Starter"
            price="$0"
            description="Begin your college application journey"
            features={[
              { text: "5 AI essay reviews", included: true },
              { text: "Intelligent feedback & scoring", included: true },
              { text: "Real-time editing interface", included: true },
              { text: "Basic application insights", included: true },
              { text: "Standard processing", included: true },
            ]}
            ctaText={
              !user
                ? "Get Started"
                : isFreeUser
                ? "Current Plan"
                : isProUser
                ? "Downgrade to Free"
                : "Get Started"
            }
            onCtaClick={handleFreeClick}
            disabled={isFreeUser}
            billingCycle="/month"
          />

          <PricingTier
            name="Pro Copilot"
            price={isFullCycle ? "$8" : "$10"}
            originalPrice={isFullCycle ? "$10" : undefined}
            description="Complete AI-powered application support"
            features={[
              { text: "Unlimited essay reviews & analysis", included: true },
              { text: "Advanced AI feedback engine", included: true },
              { text: "Priority processing & support", included: true },
              { text: "Comprehensive application insights", included: true },
              { text: "Standard processing", included: true },
            ]}
            ctaText={
              isProUser
                ? "Current Plan"
                : isFreeUser
                ? "Upgrade to Pro"
                : "Get Pro"
            }
            highlighted={true}
            onCtaClick={onProUpgrade || handleProUpgrade}
            disabled={isProUser}
            billingCycle="/month"
            discount={isFullCycle ? "20% off" : undefined}
          />

          <PricingTier
            name="Institution"
            price="Contact Us"
            description="For schools, counselors & organizations"
            features={[
              { text: "Multi-student dashboard", included: true },
              { text: "Institutional AI models", included: true },
              { text: "White-label copilot experience", included: true },
              { text: "Advanced analytics & reporting", included: true },
              { text: "Custom integration & training", included: true },
            ]}
            ctaText="Contact Sales"
            onCtaClick={onEnterprise || handleEnterprise}
          />
        </div>
      </div>
    </div>
  );
};

export default PricingSection;
